<script setup lang="ts">
import type {
  ChipContent,
  CombinedChip,
  EditorialDiscoverAlternativeProps,
} from './EditorialDiscoverAlternative.props'

const { breakpoint } = useProjectBreakpoint()

const { ts } = useI18n()

const props = withDefaults(defineProps<EditorialDiscoverAlternativeProps>(), {
  alphabeticalOrder: true,
})

const isMobile = ref()

const hoverStates = reactive<Record<number, boolean>>({})
const initHoverStates = () => {
  props.chips.forEach((chip, index) => {
    hoverStates[index] = false
  })
}

onMounted(initHoverStates)

watch(() => props.chips, initHoverStates, { immediate: true })

const defaultView = computed(() =>
  props.defaultView === 'grid' ? 'gridWall' : 'gridThree'
)

const selectedChip = ref(0)

const sortedChips = computed(() => {
  const chips = [...props.chips]
  const allContent: ChipContent = chips.flatMap(
    (chip: CombinedChip) => chip.chipContent.content
  )
  const viewAllChip: CombinedChip = {
    chip: {
      label: `${ts('view_all')}`,
      selected: false,
      isTimelineStyle: false,
    },
    chipContent: { content: allContent },
  }

  if (props.alphabeticalOrder) {
    chips.sort((a, b) => {
      if (a.chip.label.toLowerCase() < b.chip.label.toLowerCase()) {
        return -1
      }
      if (a.chip.label.toLowerCase() > b.chip.label.toLowerCase()) {
        return 1
      }
      return 0
    })
  }
  chips.unshift(viewAllChip)
  return chips
})

const selectedChipsContentLength = computed(
  () => sortedChips.value[selectedChip.value].chipContent.content.length
)
const selectedView = ref(defaultView.value ?? 'gridThree')

const isGridThreeView = computed(() => selectedView.value === 'gridThree')

const gridColumn = Number(props.gridColumn)

const baseNumberOfSlideToShow = computed(() =>
  isMobile.value ? 6 : gridColumn * 3
)

const dynamicNumberOfSlidesToShow = ref(baseNumberOfSlideToShow.value)

const slidesToShow = computed(() => {
  return sortedChips.value[selectedChip.value].chipContent.content
})

const handleSelectedChip = (index: number) => {
  selectedChip.value = index
  resetDynamicSlidesToShow()
}

const handleSelectedView = (view: string) => {
  selectedView.value = view
  resetDynamicSlidesToShow()
}

const showMoreFunction = () => {
  if (dynamicNumberOfSlidesToShow.value < selectedChipsContentLength.value) {
    dynamicNumberOfSlidesToShow.value += baseNumberOfSlideToShow.value
  }
}

const showAllFunction = () => {
  dynamicNumberOfSlidesToShow.value = selectedChipsContentLength.value
}

const resetDynamicSlidesToShow = () => {
  dynamicNumberOfSlidesToShow.value = baseNumberOfSlideToShow.value
}

const chipsLabelModifier = computed(() => {
  return sortedChips.value.map((singleChip: CombinedChip) => {
    return {
      label: `${singleChip.chip.label} (${singleChip.chipContent ? singleChip.chipContent.content.length : 'Null'})`,
      selected: false,
    }
  })
})
watch(
  () => breakpoint.smaller('md').value,
  newVal => {
    isMobile.value = newVal
    resetDynamicSlidesToShow()
  },
  { immediate: true }
)

const UILinkComponent = resolveComponent('UILink')
</script>

<template>
  <div
    class="editorial-discover-alternative text-text-primary bg-background-secondary pt-6 lg:pb-6"
  >
    <div class="flex flex-col gap-10 py-10 text-center">
      <div v-if="title || subtitle" class="flex flex-col gap-4 px-4 lg:px-16">
        <UtilsMarkdown
          v-if="title"
          :content="title"
          is-preprocessed
          container="span"
          class="text-book-4 lg:text-book-3 w-full"
        />
        <UtilsMarkdown
          v-if="subtitle"
          :content="subtitle"
          is-preprocessed
          container="span"
          class="text-light-5"
        />
      </div>
      <div v-if="chipsLabelModifier.length > 2" class="w-full px-4 lg:px-16">
        <UIChipsWrapper
          :chips="chipsLabelModifier"
          :is-timeline-style="chipStyle === 'timeline'"
          :default-selected-chip="0"
          @selected="handleSelectedChip"
        />
      </div>
      <div v-if="showGridSelector" class="flex justify-center gap-10">
        <button
          :class="isGridThreeView ? 'viewSelected' : 'viewUnselected'"
          @click="handleSelectedView('gridThree')"
        >
          <DefaultIconsGridThree class="h-5 w-5" />
        </button>
        <button
          :class="isGridThreeView ? 'viewUnselected' : 'viewSelected'"
          @click="handleSelectedView('gridWall')"
        >
          <DefaultIconsGridWall class="h-5 w-5" />
        </button>
      </div>
      <div v-if="isGridThreeView">
        <UISliderEditorialWithArrows
          :items="sortedChips[selectedChip].chipContent.content"
          :has-no-space-between="cardsConfiguration?.hasNoSpaceBetween"
          :loop="sortedChips[selectedChip].chipContent.content.length > 3"
          :show-navigation="
            sortedChips[selectedChip].chipContent.content.length > 3
          "
          :type="cardsConfiguration?.type"
          :card-dimensions="cardsConfiguration?.cardDimensions"
          :mobile-centered="cardsConfiguration?.mobileCentered"
          :padding="cardsConfiguration?.padding"
        >
          <template #default="{ item, index }">
            <div class="card-container relative flex flex-col gap-8">
              <component
                :is="item.link?.length ? UILinkComponent : 'div'"
                class="underline-effect-appear flex flex-col gap-8"
                :type="item.link && item.link[0].type"
                :target="item.link && item.link[0].target"
                :internal-link="
                  item.link && item.link.length && item.link[0].internalLink
                "
                @mouseover="hoverStates[index] = true"
                @mouseleave="hoverStates[index] = false"
              >
                <div
                  :class="{
                    'card main-media-card gap-[1px]': item.hoverElement,
                  }"
                  class="image-slider-container"
                >
                  <UIMedia v-bind="item.element" :aspect-ratio="aspectRatio" />
                </div>
                <div
                  v-if="item.hoverElement"
                  class="card hover-image-card absolute top-0"
                >
                  <UIMedia
                    v-bind="item.hoverElement"
                    :aspect-ratio="aspectRatio"
                  />
                </div>

                <div class="flex flex-col items-center gap-2 pb-10">
                  <UtilsMarkdown
                    v-if="item.title"
                    :content="item.title"
                    is-preprocessed
                    container="span"
                    class="md:text-book-5 text-book-6"
                    :class="{
                      'text-effect': item.link?.length,
                    }"
                  />
                  <UtilsMarkdown
                    v-if="item.subtitle"
                    :content="item.subtitle"
                    is-preprocessed
                    container="span"
                    class="md:text-light-5 text-light-6"
                    :class="{
                      'text-effect': item.link?.length,
                    }"
                  />
                </div>
              </component>
            </div>
          </template>
        </UISliderEditorialWithArrows>
      </div>
      <div
        v-else
        class="grid grid-cols-2 gap-[1px]"
        :class="[
          { 'md:grid-cols-3': gridColumn === 3 },
          { 'md:grid-cols-4': gridColumn === 4 },
          { 'px-4 lg:px-16': gridPadding },
        ]"
      >
        <div
          v-for="(content, contentIndex) in slidesToShow"
          :key="`${content.title}-${contentIndex}`"
          class="block"
          :class="[
            { hidden: contentIndex >= dynamicNumberOfSlidesToShow },
            gridColumn === 3 && gridPadding
              ? 'md:grid-slide-3-padded'
              : 'md:grid-slide-3',
            gridColumn === 4 && gridPadding
              ? 'md:grid-slide-4-padded'
              : 'md:grid-slide-4',
          ]"
        >
          <div class="card-container relative flex flex-col gap-8">
            <component
              :is="content.link?.length ? UILinkComponent : 'div'"
              class="underline-effect-appear flex flex-col gap-8"
              :type="
                content.link && content.link.length && content.link[0].type
              "
              :target="
                content.link && content.link.length && content.link[0].target
              "
              :internal-link="
                content.link &&
                content.link.length &&
                content.link[0].internalLink
              "
              :aria-label="
                content.link && content.link.length && content.link[0].ariaLabel
              "
              @mouseover="hoverStates[contentIndex] = true"
              @mouseleave="hoverStates[contentIndex] = false"
            >
              <div :class="{ 'card main-media-card': content.hoverElement }">
                <UIMedia v-bind="content.element" :aspect-ratio="aspectRatio" />
              </div>
              <div
                v-if="content.hoverElement"
                class="card hover-image-card absolute top-0"
              >
                <UIMedia
                  v-bind="content.hoverElement"
                  :aspect-ratio="aspectRatio"
                />
              </div>

              <div
                v-if="content.title || content.subtitle"
                class="flex flex-col items-center gap-2 pb-10"
              >
                <span
                  v-if="content.title"
                  class="md:text-book-5 text-book-6 text-effect w-fit"
                >
                  {{ content.title }}
                </span>
                <span
                  v-if="content.subtitle"
                  class="md:text-light-5 text-light-6 text-effect w-fit"
                >
                  {{ content.subtitle }}
                </span>
              </div>
            </component>
          </div>
        </div>
      </div>
      <div
        v-if="
          !isGridThreeView &&
          dynamicNumberOfSlidesToShow < selectedChipsContentLength
        "
        class="flex flex-col items-center gap-2"
      >
        <AtomsCta :icon-show-default="false" @click="showMoreFunction">
          <template #label>
            {{
              `${$ts('plp.showMore')} (${dynamicNumberOfSlidesToShow}/${selectedChipsContentLength})`
            }}
          </template>
        </AtomsCta>
        <AtomsCta
          anatomy="link"
          class="py-sm"
          :icon-show-default="false"
          @click="showAllFunction"
        >
          <template #label>
            {{ `${$ts('plp.showAll')} (${selectedChipsContentLength})` }}
          </template>
        </AtomsCta>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.viewSelected {
  @apply text-text-primary;
}

.viewUnselected {
  @apply text-text-inactive;
}

:deep(.swiper) {
  width: 100%;
  height: 100%;
}

:deep(.swiper-slide) {
  width: 66.67%; // 100% / 1.5

  @screen md {
    width: 40%; // 100% / 2.5
  }

  @screen lg {
    width: 28.57%; // 100% / 3.5
  }
}

:deep(.grid-slide-3-padded) {
  width: calc((100vw - 32px) / 2);

  @screen md {
    width: calc((100vw - 32px) / 3);
  }

  @screen lg {
    width: calc((100vw - 128px) / 3);
  }
}

:deep(.grid-slide-4-padded) {
  width: calc((100vw) / 2);

  @screen md {
    width: calc((100vw - 32px) / 4);
  }

  @screen ml {
    width: calc((100vw - 128px) / 4);
  }
}

:deep(.grid-slide-3) {
  width: 50vw;

  @screen md {
    width: 33.33vw;
  }

  @screen lg {
    width: 33.33vw;
  }
}

:deep(.grid-slide-4) {
  width: 50vw;

  @screen md {
    width: 25vh;
  }

  @screen lg {
    width: 25vh;
  }
}

.card-container {
  .card {
    transition: opacity 0.4s ease;
  }
  .main-media-card {
    opacity: 1;
  }
  .hover-image-card {
    opacity: 0;
  }
  &:hover {
    .main-media-card {
      opacity: 0;
    }
    .hover-image-card {
      opacity: 1;
    }
  }
}

.swiper-button-next-unique::after,
.swiper-button-prev-unique::after,
.swiper-button-next-unique,
.swiper-button-prev-unique {
  --swiper-navigation-size: 14px;
  --swiper-navigation-sides-offset: 20px;
  --swiper-navigation-top-offset: 45%;
  @apply text-text-primary;
}
</style>
